import cx from 'classnames2';
import React from 'react';

import { ITestProps } from '../utils/dataTest';

import { Icon } from '../Icon';
import { ValidationStates } from '../Input';

import styles from './TextArea.module.scss';

type OnChangeCallback = (newValue: string) => void;

export interface ITextAreaProps extends ITestProps {
  /** Set to true if you want to disable the textarea from accepting changes from the user */
  disabled?: boolean;
  /** Id attribute assigned to the textarea. Used to associate the textarea with other form elements (Ex: label) */
  id?: string;
  name?: string;
  /** Maximum number of characters the value can have */
  maxLength?: number;
  /** Callback function for when input loses focus */
  onBlur?: OnChangeCallback;
  /** Callback function for when input value is changed */
  onChange?: OnChangeCallback;
  /** Text displayed when value is empty */
  placeholder?: string;
  /** Number of rows textarea should have */
  rowCount?: number;
  size?: 'small' | 'default' | 'large';
  /** Validation state of the input which can be either success or error */
  validationState?: ValidationStates;
  /** The current value of the input that is displayed to the user */
  value?: string;
  /** Determines visual style of textarea */
  variant?: 'neutral' | 'naked';
  onKeyDown?(event: React.KeyboardEvent<HTMLTextAreaElement>): void;
  onKeyUp?(event: React.KeyboardEvent<HTMLTextAreaElement>): void;
}

const createOnChangeHandler = (callback: OnChangeCallback) => (e) => callback(e.target.value);

export const TextArea: React.FC<ITextAreaProps> = (props) => {
  const containerClassnames = {
    [styles.errorState]: props.validationState === ValidationStates.Error,
    [styles.successState]: props.validationState === ValidationStates.Success
  };

  const textareaClassnames = {
    [styles.disabled]: props.disabled,
    [styles.neutral]: props.variant === 'neutral',
    [styles.naked]: props.variant === 'naked',
    [styles.sizeSmall]: props.size === 'small',
    [styles.sizeDefault]: props.size === 'default',
    [styles.sizeLarge]: props.size === 'large'
  };

  return (
    <div className={cx(styles.container, containerClassnames)}>
      <textarea
        className={cx(textareaClassnames)}
        id={props.id}
        name={props.name}
        data-test={props.dataTest}
        disabled={props.disabled}
        placeholder={props.placeholder}
        rows={props.rowCount}
        value={props.value}
        maxLength={props.maxLength}
        onChange={props.onChange && createOnChangeHandler(props.onChange)}
        onBlur={props.onBlur && createOnChangeHandler(props.onBlur)}
        onKeyDown={props.onKeyDown}
        onKeyUp={props.onKeyUp}
      />
      <span className={cx(styles.iconWrapper)}>
        {props.validationState === ValidationStates.Success && <Icon className={styles.icon} icon="status-success" />}
        {props.validationState === ValidationStates.Error && <Icon className={styles.icon} icon="status-error" />}
      </span>
    </div>
  );
};

TextArea.defaultProps = {
  variant: 'neutral',
  size: 'default',
  rowCount: 3
};
